import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Eye, Loader2, AlertTriangle, X, ChevronDown, PenLine, FileStack, Send, ArrowRight, Paperclip } from "lucide-react";
import { focusField } from "../lib/focusField";
import { openDocSlot } from "./MissingDocsCard";
import DossierPoseModal from "./DossierPoseModal";
import DocumentsMenu from "./DocumentsMenu";
import SendDocModal from "./SendDocModal";
import SignatureStatus from "./SignatureStatus";
import { toast } from "sonner";
import { api, API } from "../lib/api";

const SIGN = { aucune: "Sans signature", papier: "Signature papier", yousign: "Yousign", papier_yousign: "Papier puis Yousign" };

function openPreview(dossierId, tpl) {
  const win = window.open("", "_blank");
  fetch(`${API}/dossiers/${dossierId}/documents/${tpl.id}/pdf`, { headers: { Authorization: `Bearer ${localStorage.getItem("norea_token")}` } })
    .then(async (r) => { if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail); return r.blob(); })
    .then((b) => {
      const url = URL.createObjectURL(b);
      if (win) { win.document.title = tpl.name; win.document.body.style.margin = "0"; win.document.body.innerHTML = `<iframe src="${url}" title="${tpl.name}" style="border:0;position:fixed;inset:0;width:100vw;height:100vh"></iframe>`; }
    })
    .catch((e) => { win?.close(); toast.error(e.message || "Génération impossible"); });
}

function MissingDialog({ tpl, dossierId, onClose }) {
  const goField = useGoField();
  const go = (m) => { onClose(); goField(m); };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md border border-norea-border p-5" onClick={(e) => e.stopPropagation()} data-testid="doc-missing-dialog">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-lg font-medium">{tpl.name}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-norea-surfaceHover" data-testid="doc-missing-close"><X className="w-4 h-4" /></button>
        </div>
        {tpl.missing.length > 0 && <>
          <p className="text-sm text-norea-textMuted mb-2">Champs vides — ils apparaîtront en pointillés :</p>
          <ul className="space-y-1 mb-3">
            {tpl.missing.map((m) => (
              <li key={m.key} className="flex items-center justify-between text-sm" data-testid={`doc-missing-${m.key}`}>
                <span>{m.label}</span>
                {m.tab && <button onClick={() => go(m)} className="text-xs text-norea-primary hover:underline" data-testid={`doc-missing-link-${m.key}`}>Compléter →</button>}
              </li>
            ))}
          </ul>
        </>}
        {tpl.missing_pieces?.length > 0 && (
          <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-3 flex gap-1.5" data-testid="doc-missing-pieces">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> Pièces à joindre manquantes : {tpl.missing_pieces.join(", ")}. L'envoi reste possible.
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary text-sm">Annuler</button>
          <button onClick={() => { openPreview(dossierId, tpl); onClose(); }} className="btn-primary text-sm" data-testid="doc-generate-anyway"><Eye className="w-4 h-4" /> Générer quand même</button>
        </div>
      </div>
    </div>
  );
}

function useGoField() {
  const navigate = useNavigate();
  return (m) => (m.tab === "configuration" ? navigate("/configuration") : m.tab ? focusField(m.tab, m.anchors) : null);
}

const Chip = ({ onClick, children, cls, testid, title }) => (
  <button onClick={onClick} title={title} className={`inline-flex items-center gap-0.5 rounded-full border px-2 py-0.5 text-[10px] font-medium transition-colors ${cls}`} data-testid={testid}>{children}</button>
);

function MissingChips({ tpl }) {
  const goField = useGoField();
  if (!tpl.missing.length && !tpl.missing_pieces?.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1 mt-1" data-testid={`doc-missing-list-${tpl.id}`}>
      {tpl.missing.map((m) => (
        <Chip key={m.key} onClick={() => goField(m)} title="Aller au champ" cls="border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100" testid={`doc-missing-chip-${tpl.id}-${m.key}`}>
          {m.label}<ArrowRight className="w-2.5 h-2.5" />
        </Chip>
      ))}
      {(tpl.missing_pieces || []).map((p) => (
        <Chip key={p} onClick={() => openDocSlot(p)} title="Ouvrir l'emplacement du document" cls="border-orange-300 bg-orange-50 text-orange-800 hover:bg-orange-100" testid={`doc-missing-piece-${tpl.id}-${p}`}>
          <Paperclip className="w-2.5 h-2.5" />{p}
        </Chip>
      ))}
    </div>
  );
}

function DocRow({ tpl, dossierId, onAsk, onSend, sig, onSig }) {
  const goField = useGoField();
  const warn = tpl.missing.length + (tpl.missing_pieces?.length || 0);
  const click = () => (warn ? onAsk(tpl) : openPreview(dossierId, tpl));
  const goFirst = () => (tpl.missing.length ? goField(tpl.missing[0]) : openDocSlot(tpl.missing_pieces[0]));
  return (
    <div className="flex items-center gap-3 px-3 py-2 rounded hover:bg-norea-surfaceHover" data-testid={`doc-row-${tpl.id}`}>
      <FileText className="w-4 h-4 text-norea-primary shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm truncate">{tpl.name}</div>
        <div className="text-[11px] text-norea-textMuted">{tpl.destinataire} · {SIGN[tpl.signature] || tpl.signature}{tpl.method === "overlay" ? " · PDF d'origine complété" : ""}</div>
        <MissingChips tpl={tpl} />
      </div>
      {warn > 0 && <button onClick={goFirst} title="Aller au premier élément à compléter" className="text-[11px] text-amber-700 inline-flex items-center gap-1 hover:underline shrink-0" data-testid={`doc-warn-${tpl.id}`}><AlertTriangle className="w-3 h-3" />{tpl.missing.length ? `${tpl.missing.length} champ(s) vide(s)` : `${tpl.missing_pieces.length} pièce(s) manquante(s)`}</button>}
      <button onClick={click} className="btn-secondary text-xs" data-testid={`doc-preview-${tpl.id}`}><Eye className="w-3.5 h-3.5" /> Aperçu</button>
      {sig && <SignatureStatus sig={sig} onChange={onSig} />}
      <button onClick={() => onSend(tpl)} className="btn-secondary text-xs" data-testid={`doc-send-${tpl.id}`}><Send className="w-3.5 h-3.5" /> Envoyer</button>
    </div>
  );
}

export default function DossierDocumentsCard({ dossier }) {
  const [data, setData] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [ask, setAsk] = useState(null);
  const [pose, setPose] = useState(false);
  const [send, setSend] = useState(null);
  const [sigs, setSigs] = useState({});
  const load = useCallback(() => api.get(`/dossiers/${dossier.id}/doc-templates`).then((r) => setData(r.data)).catch(() => setData({ items: [] })), [dossier.id]);
  const loadSigs = useCallback(() => api.get(`/dossiers/${dossier.id}/signatures`).then((r) => {
    const m = {};
    r.data.items.forEach((s) => { if (!m[s.doc_id]) m[s.doc_id] = s; });
    setSigs(m);
  }).catch(() => {}), [dossier.id]);
  useEffect(() => { load(); loadSigs(); }, [load, loadSigs, dossier.updated_at]);
  const groups = useMemo(() => {
    const list = (data?.items || []).filter((t) => showAll || t.applicable);
    return [...new Set(list.map((t) => t.group))].map((g) => [g, list.filter((t) => t.group === g)]);
  }, [data, showAll]);
  if (!data) return null;
  return (
    <div className="card-flat p-5" data-testid="dossier-documents-card">
      <div className="flex items-center justify-between mb-3">
        <div className="overline flex items-center gap-1.5"><PenLine className="w-3 h-3" /> Documents à générer {data.produit && `· ${data.produit}`}{data.financeur && ` · ${data.financeur}`}</div>
        <div className="flex items-center gap-3">
          <button onClick={() => setShowAll((s) => !s)} className="text-xs text-norea-textMuted hover:text-norea-primary inline-flex items-center gap-1" data-testid="doc-toggle-all">
            {showAll ? "Seulement ceux du produit" : "Tous les modèles (autre produit)"} <ChevronDown className={`w-3 h-3 transition-transform ${showAll ? "rotate-180" : ""}`} />
          </button>
          <DocumentsMenu dossier={dossier} />
          <button onClick={() => setPose(true)} className="btn-primary text-xs" data-testid="open-dossier-pose"><FileStack className="w-3.5 h-3.5" /> Dossier de pose</button>
        </div>
      </div>
      {groups.map(([g, items]) => (
        <div key={g} className="mb-2">
          <div className="text-[11px] uppercase tracking-wider text-norea-textMuted px-3 mb-1">{g}</div>
          {items.map((t) => <DocRow key={t.id} tpl={t} dossierId={dossier.id} onAsk={setAsk} onSend={setSend} sig={sigs[t.id]} onSig={loadSigs} />)}
        </div>
      ))}
      {!groups.length && <div className="text-sm text-norea-textMuted italic">Aucun modèle pour ce produit</div>}
      {ask && <MissingDialog tpl={ask} dossierId={dossier.id} onClose={() => { setAsk(null); load(); }} />}
      {send && <SendDocModal dossierId={dossier.id} docIds={[send.id]} title={`Envoyer — ${send.name}`} onClose={() => setSend(null)} onSent={loadSigs} />}
      {pose && <DossierPoseModal dossierId={dossier.id} onClose={() => setPose(false)} />}
      {!data.items.length && <Loader2 className="w-4 h-4 animate-spin" />}
    </div>
  );
}
